import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { 
  getCustomerList, 
  getCustomerDetail, 
  updateCustomer,
  getCustomerBookings,
  getCustomerCharges
} from '@/api/customer'
import { imageCacheUtil } from '@/utils/image-cache'

export const useCustomerStore = defineStore('customer', () => {
  // State
  const customers = ref([])
  const currentCustomer = ref(null)
  const loading = ref(false)
  const total = ref(0)
  
  // Filters
  const filters = ref({
    name: '',
    phone: '',
    status: ''
  })

  // Getters
  const activeCustomers = computed(() => 
    customers.value.filter(s => s.status === 1)
  )
  
  const inactiveCustomers = computed(() => 
    customers.value.filter(s => s.status === 0)
  )
  
  const blockedCustomers = computed(() => 
    customers.value.filter(s => s.status === 0)
  )
  
  const customersCount = computed(() => customers.value.length)
  
  const newCustomersThisMonth = computed(() => {
    const now = new Date()
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    return customers.value.filter(s => 
      s.createTime && new Date(s.createTime) >= monthStart
    ).length
  })

  // Actions
  const fetchCustomers = async (filterOptions = {}, forceRefresh = false) => {
    loading.value = true
    try {
      const params = {
        page: filterOptions.page || 1,
        pageSize: filterOptions.pageSize || 10,
        ...filterOptions
      }
      
      const res = await getCustomerList(params)
      
      if (res.success) {
        // Handle both array and object with list property
        if (Array.isArray(res.data)) {
          customers.value = res.data
          total.value = res.data.length
        } else {
          customers.value = res.data?.list || []
          total.value = res.data?.total || 0
        }
        
        // Warm cache with all customer avatars (force overwrite if refreshing)
        const avatarUrls = customers.value.map(c => c.avatar).filter(Boolean)
        imageCacheUtil.warmCache(avatarUrls, forceRefresh)
        
        if (Object.keys(filterOptions).length > 0) {
          filters.value = { ...filters.value, ...filterOptions }
        }
        
        return { success: true, data: customers.value }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Customer Store] Fetch error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchCustomerById = async (id) => {
    loading.value = true
    try {
      const res = await getCustomerDetail(id)
      
      if (res.success) {
        currentCustomer.value = res.data
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Customer Store] Fetch detail error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const modifyCustomer = async (id, customerData) => {
    try {
      const res = await updateCustomer(id, customerData)
      
      if (res.success) {
        // Update in local list
        const index = customers.value.findIndex(s => s.id === id)
        if (index !== -1) {
          customers.value[index] = { ...customers.value[index], ...customerData }
        }
        
        // Update current if matches
        if (currentCustomer.value?.id === id) {
          currentCustomer.value = { ...currentCustomer.value, ...customerData }
        }
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Customer Store] Update error:', error)
      return { success: false, error: error.message }
    }
  }

  const fetchCustomerBookingsAction = async (id, startDate, endDate) => {
    loading.value = true
    try {
      const res = await getCustomerBookings(id, { startDate, endDate })
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Customer Store] Bookings error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const fetchCustomerChargesAction = async (id, startDate, endDate) => {
    loading.value = true
    try {
      const res = await getCustomerCharges(id, { startDate, endDate })
      
      if (res.success) {
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('[Customer Store] Charges error:', error)
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  const clearCurrentCustomer = () => {
    currentCustomer.value = null
  }

  const resetFilters = () => {
    filters.value = {
      name: '',
      phone: '',
      status: ''
    }
  }

  return {
    // State
    customers,
    currentCustomer,
    loading,
    total,
    filters,
    
    // Getters
    activeCustomers,
    inactiveCustomers,
    blockedCustomers,
    customersCount,
    newCustomersThisMonth,
    
    // Actions
    fetchCustomers,
    fetchCustomerById,
    modifyCustomer,
    fetchCustomerBookingsAction,
    fetchCustomerChargesAction,
    clearCurrentCustomer,
    resetFilters
  }
})